import { useEffect, useRef } from 'react'
import { useZinsserStore } from '@/stores/useZinsserStore'
import { ZinsserChatMessage } from './ZinsserChatMessage'
import { ZinsserChatInput } from './ZinsserChatInput'
import { ZinsserThinkingIndicator } from './ZinsserThinkingIndicator'
import { ZinsserThinkingBlock } from './ZinsserThinkingBlock'
import { streamZinsserGatekeeper } from '@/lib/api'
import type { FinalDiff } from '@/lib/api'

export function ZinsserChat() {
  const {
    sessionId,
    brainstormContent,
    workflowStatus,
    isStandaloneWorkflow,
    runMode,
    setWorkflowStatus,
    setError,
    // Gatekeeper state
    gatekeeperTurns,
    gatekeeperStreamingThinking,
    gatekeeperIsThinking,
    // Gatekeeper streaming actions
    addGatekeeperUserTurn,
    startGatekeeperStreaming,
    startGatekeeperThinking,
    updateGatekeeperThinking,
    finishGatekeeperThinking,
    finishGatekeeperStreaming,
    // Zinsser state
    zinsserStreamingThinking,
    zinsserIsThinking,
    zinsserIsProcessing,
    zinsserHasThinking,
    // Zinsser streaming actions
    startZinsserProcessing,
    startZinsserThinking,
    updateZinsserThinking,
    finishZinsserThinking,
    finishZinsserProcessing,
    // CutClutter state (micro editing - Pass 1)
    cutClutterStreamingThinking,
    cutClutterIsThinking,
    cutClutterIsProcessing,
    cutClutterHasThinking,
    // CutClutter streaming actions
    startCutClutterProcessing,
    startCutClutterThinking,
    updateCutClutterThinking,
    finishCutClutterThinking,
    finishCutClutterProcessing,
    // StrengthenVerbs state (micro editing - Pass 2)
    strengthenVerbsStreamingThinking,
    strengthenVerbsIsThinking,
    strengthenVerbsIsProcessing,
    strengthenVerbsHasThinking,
    // StrengthenVerbs streaming actions
    startStrengthenVerbsProcessing,
    startStrengthenVerbsThinking,
    updateStrengthenVerbsThinking,
    finishStrengthenVerbsThinking,
    finishStrengthenVerbsProcessing,
    // CheckLogic state (micro editing - Pass 3)
    checkLogicStreamingThinking,
    checkLogicIsThinking,
    checkLogicIsProcessing,
    checkLogicHasThinking,
    // CheckLogic streaming actions
    startCheckLogicProcessing,
    startCheckLogicThinking,
    updateCheckLogicThinking,
    finishCheckLogicThinking,
    finishCheckLogicProcessing,
    // RhythmSound state (micro editing - Pass 4)
    rhythmSoundStreamingThinking,
    rhythmSoundIsThinking,
    rhythmSoundIsProcessing,
    rhythmSoundHasThinking,
    // RhythmSound streaming actions
    startRhythmSoundProcessing,
    startRhythmSoundThinking,
    updateRhythmSoundThinking,
    finishRhythmSoundThinking,
    finishRhythmSoundProcessing,
    // Responder state (coaching response after editing)
    responderStreamingThinking,
    responderIsThinking,
    responderIsProcessing,
    // Responder streaming actions
    startResponderProcessing,
    startResponderThinking,
    updateResponderThinking,
    finishResponderThinking,
    finishResponderProcessing,
    // Real-time canvas editing
    applyCanvasEdit,
    applyCanvasInsert,
    // Session management
    refreshSessionNameIfNeeded,
    // Final diff state (setFinalDiff stores it, then finishResponderProcessing attaches it to the turn)
    setFinalDiff,
    // Annotation state
    setAnnotatedDoc,
  } = useZinsserStore()

  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new messages arrive or content is streaming
  // Use instant scroll during streaming for smoother following, smooth scroll otherwise
  useEffect(() => {
    const isStreaming = workflowStatus === 'streaming'
    messagesEndRef.current?.scrollIntoView({
      behavior: isStreaming ? 'instant' : 'smooth',
      block: 'end'
    })
  }, [
    gatekeeperTurns,
    gatekeeperStreamingThinking,
    gatekeeperIsThinking,
    zinsserStreamingThinking,
    zinsserIsThinking,
    cutClutterStreamingThinking,
    cutClutterIsThinking,
    strengthenVerbsStreamingThinking,
    strengthenVerbsIsThinking,
    checkLogicStreamingThinking,
    checkLogicIsThinking,
    rhythmSoundStreamingThinking,
    rhythmSoundIsThinking,
    workflowStatus
  ])

  const handleSubmit = async (content: string) => {
    if (!content.trim() || workflowStatus === 'submitting' || workflowStatus === 'processing' || workflowStatus === 'streaming') {
      return
    }

    if (!sessionId) {
      setError('No session ID available')
      return
    }

    addGatekeeperUserTurn(content)
    setWorkflowStatus('submitting')

    try {
      startGatekeeperStreaming()

      await streamZinsserGatekeeper(
        {
          content,
          session_id: sessionId,
          new_doc: brainstormContent || undefined,
          run_mode: runMode,
        },
        {
          // Gatekeeper thinking
          onThinkingStart: () => startGatekeeperThinking(),
          onThinkingDelta: (delta) => updateGatekeeperThinking(delta),
          onThinkingComplete: () => finishGatekeeperThinking(),
          // Gatekeeper done
          onGatekeeperDone: async (output) => {
            finishGatekeeperStreaming(output)
            await refreshSessionNameIfNeeded()
          },
          // Zinsser start (fires before thinking, works in fast mode too)
          onZinsserStart: () => {
            startZinsserProcessing()
          },
          // Zinsser thinking (macro editing phase)
          onZinsserThinkingStart: () => {
            startZinsserProcessing()
            startZinsserThinking()
          },
          onZinsserThinkingDelta: (delta) => updateZinsserThinking(delta),
          onZinsserThinkingComplete: () => finishZinsserThinking(),
          // Zinsser edits - apply to canvas in real-time
          onZinsserEdit: (oldStr, newStr) => {
            applyCanvasEdit(oldStr, newStr)
          },
          // Zinsser inserts - for creation mode when building from blank canvas
          onZinsserInsert: (insertLine, insertText) => {
            applyCanvasInsert(insertLine, insertText)
          },
          // Zinsser done
          onZinsserDone: (output) => {
            finishZinsserProcessing(output)
          },
          // CutClutter (micro editing - Pass 1)
          onCutClutterStart: () => {
            startCutClutterProcessing()
          },
          onCutClutterThinkingStart: () => {
            startCutClutterProcessing()
            startCutClutterThinking()
          },
          onCutClutterThinkingDelta: (delta) => updateCutClutterThinking(delta),
          onCutClutterThinkingComplete: () => finishCutClutterThinking(),
          // CutClutter edits - apply to canvas in real-time
          onCutClutterEdit: (oldStr, newStr) => {
            applyCanvasEdit(oldStr, newStr)
          },
          // CutClutter done
          onCutClutterDone: (output) => {
            finishCutClutterProcessing(output)
          },
          // StrengthenVerbs (micro editing - Pass 2)
          onStrengthenVerbsStart: () => {
            startStrengthenVerbsProcessing()
          },
          onStrengthenVerbsThinkingStart: () => {
            startStrengthenVerbsProcessing()
            startStrengthenVerbsThinking()
          },
          onStrengthenVerbsThinkingDelta: (delta) => updateStrengthenVerbsThinking(delta),
          onStrengthenVerbsThinkingComplete: () => finishStrengthenVerbsThinking(),
          // StrengthenVerbs edits - apply to canvas in real-time
          onStrengthenVerbsEdit: (oldStr, newStr) => {
            applyCanvasEdit(oldStr, newStr)
          },
          // StrengthenVerbs done
          onStrengthenVerbsDone: (output) => {
            finishStrengthenVerbsProcessing(output)
          },
          // CheckLogic (micro editing - Pass 3)
          onCheckLogicStart: () => {
            startCheckLogicProcessing()
          },
          onCheckLogicThinkingStart: () => {
            startCheckLogicProcessing()
            startCheckLogicThinking()
          },
          onCheckLogicThinkingDelta: (delta) => updateCheckLogicThinking(delta),
          onCheckLogicThinkingComplete: () => finishCheckLogicThinking(),
          // CheckLogic edits - apply to canvas in real-time
          onCheckLogicEdit: (oldStr, newStr) => {
            applyCanvasEdit(oldStr, newStr)
          },
          // CheckLogic done
          onCheckLogicDone: (output) => {
            finishCheckLogicProcessing(output)
          },
          // RhythmSound (micro editing - Pass 4)
          onRhythmSoundStart: () => {
            startRhythmSoundProcessing()
          },
          onRhythmSoundThinkingStart: () => {
            startRhythmSoundProcessing()
            startRhythmSoundThinking()
          },
          onRhythmSoundThinkingDelta: (delta) => updateRhythmSoundThinking(delta),
          onRhythmSoundThinkingComplete: () => finishRhythmSoundThinking(),
          // RhythmSound edits - apply to canvas in real-time
          onRhythmSoundEdit: (oldStr, newStr) => {
            applyCanvasEdit(oldStr, newStr)
          },
          // RhythmSound done
          onRhythmSoundDone: (output) => {
            finishRhythmSoundProcessing(output)
          },
          // Session complete - workflow finished, includes final diff and annotated doc
          onSessionComplete: (output) => {
            if (output.final_diff) {
              setFinalDiff(output.final_diff as FinalDiff)
            }
            if (output.annotated_doc) {
              setAnnotatedDoc(output.annotated_doc)
            }
          },
          // Responder (coaching response after editing)
          onResponderStart: () => {
            startResponderProcessing()
          },
          onResponderThinkingStart: () => {
            startResponderProcessing()
            startResponderThinking()
          },
          onResponderThinkingDelta: (delta) => updateResponderThinking(delta),
          onResponderThinkingComplete: () => finishResponderThinking(),
          onResponderDone: (output) => {
            finishResponderProcessing(output)
          },
          // Legacy (backwards compat) - no-op since onGatekeeperDone handles it
          onDone: () => {},
          onError: (error) => setError(error),
        }
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* Messages area */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-0 md:pr-6 scroll-touch overscroll-contain">
        <div className="py-4 md:py-6">
          {gatekeeperTurns.length === 0 && workflowStatus !== 'submitting' && workflowStatus !== 'streaming' ? (
            <div className="text-center py-12">
              <h2 className="text-lg font-light text-foreground mb-2">
                What are you working on?
              </h2>
              <p className="text-sm text-muted-foreground">
                Tell me about your writing project and I'll help you get started.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {gatekeeperTurns.map((turn, index) => {
                const isLastTurn = index === gatekeeperTurns.length - 1
                const isProcessing = workflowStatus === 'streaming' || workflowStatus === 'processing'
                return (
                  <ZinsserChatMessage
                    key={index}
                    turn={turn}
                    turnIndex={index}
                    isCurrentTurn={isLastTurn}
                    isProcessing={isProcessing}
                  />
                )
              })}
              {workflowStatus === 'submitting' && (
                <ZinsserThinkingIndicator />
              )}
              {(workflowStatus === 'streaming' || workflowStatus === 'processing') && (
                <div className="flex gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Gatekeeper - hide for standalone workflows (Cut Clutter / Strengthen Verbs via context menu) */}
                    {/* Pulsing red while active, green when any editing node has started */}
                    {!isStandaloneWorkflow && (
                      !zinsserHasThinking && !cutClutterHasThinking && !strengthenVerbsHasThinking && !checkLogicHasThinking && !rhythmSoundHasThinking ? (
                        <>
                          {gatekeeperStreamingThinking && (
                            <ZinsserThinkingBlock
                              thinking={gatekeeperStreamingThinking}
                              isStreaming={gatekeeperIsThinking}
                            />
                          )}
                          <div className="text-[10px] font-light uppercase tracking-[0.08em] text-red-600 flex items-center gap-2">
                            <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                            <span>Gatekeeper</span>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                          <span className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600">Gatekeeper</span>
                        </div>
                      )
                    )}

                    {/* Zinsser - pulsing amber while active, green when complete */}
                    {zinsserIsProcessing && (
                      <div className="mt-3">
                        {zinsserStreamingThinking && (
                          <ZinsserThinkingBlock
                            thinking={zinsserStreamingThinking}
                            isStreaming={zinsserIsThinking}
                          />
                        )}
                        <div className="text-[10px] font-light uppercase tracking-[0.08em] text-amber-600 flex items-center gap-2">
                          <span className="inline-block w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                          <span>Zinsser</span>
                        </div>
                      </div>
                    )}
                    {zinsserHasThinking && !zinsserIsProcessing && (
                      <div className="mt-3 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                        <span className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600">Zinsser</span>
                      </div>
                    )}

                    {/* CutClutter - pulsing rose while active, green when complete */}
                    {cutClutterIsProcessing && (
                      <div className="mt-3">
                        {cutClutterStreamingThinking && (
                          <ZinsserThinkingBlock
                            thinking={cutClutterStreamingThinking}
                            isStreaming={cutClutterIsThinking}
                          />
                        )}
                        <div className="text-[10px] font-light uppercase tracking-[0.08em] text-rose-600 flex items-center gap-2">
                          <span className="inline-block w-2 h-2 bg-rose-500 rounded-full animate-pulse" />
                          <span>Clutter</span>
                        </div>
                      </div>
                    )}
                    {cutClutterHasThinking && !cutClutterIsProcessing && (
                      <div className="mt-3 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                        <span className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600">Clutter</span>
                      </div>
                    )}

                    {/* StrengthenVerbs - pulsing violet while active, green when complete */}
                    {strengthenVerbsIsProcessing && (
                      <div className="mt-3">
                        {strengthenVerbsStreamingThinking && (
                          <ZinsserThinkingBlock
                            thinking={strengthenVerbsStreamingThinking}
                            isStreaming={strengthenVerbsIsThinking}
                          />
                        )}
                        <div className="text-[10px] font-light uppercase tracking-[0.08em] text-violet-600 flex items-center gap-2">
                          <span className="inline-block w-2 h-2 bg-violet-500 rounded-full animate-pulse" />
                          <span>Verbs</span>
                        </div>
                      </div>
                    )}
                    {strengthenVerbsHasThinking && !strengthenVerbsIsProcessing && (
                      <div className="mt-3 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                        <span className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600">Verbs</span>
                      </div>
                    )}

                    {/* CheckLogic - pulsing cyan while active, green when complete */}
                    {checkLogicIsProcessing && (
                      <div className="mt-3">
                        {checkLogicStreamingThinking && (
                          <ZinsserThinkingBlock
                            thinking={checkLogicStreamingThinking}
                            isStreaming={checkLogicIsThinking}
                          />
                        )}
                        <div className="text-[10px] font-light uppercase tracking-[0.08em] text-cyan-600 flex items-center gap-2">
                          <span className="inline-block w-2 h-2 bg-cyan-500 rounded-full animate-pulse" />
                          <span>Logic</span>
                        </div>
                      </div>
                    )}
                    {checkLogicHasThinking && !checkLogicIsProcessing && (
                      <div className="mt-3 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                        <span className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600">Logic</span>
                      </div>
                    )}

                    {/* RhythmSound - pulsing indigo while active, green when complete */}
                    {rhythmSoundIsProcessing && (
                      <div className="mt-3">
                        {rhythmSoundStreamingThinking && (
                          <ZinsserThinkingBlock
                            thinking={rhythmSoundStreamingThinking}
                            isStreaming={rhythmSoundIsThinking}
                          />
                        )}
                        <div className="text-[10px] font-light uppercase tracking-[0.08em] text-indigo-600 flex items-center gap-2">
                          <span className="inline-block w-2 h-2 bg-indigo-500 rounded-full animate-pulse" />
                          <span>Rhythm</span>
                        </div>
                      </div>
                    )}
                    {rhythmSoundHasThinking && !rhythmSoundIsProcessing && (
                      <div className="mt-3 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                        <span className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600">Rhythm</span>
                      </div>
                    )}

                    {/* Responder - pulsing pink while generating response */}
                    {responderIsProcessing && (
                      <div className="mt-3">
                        {responderStreamingThinking && (
                          <ZinsserThinkingBlock
                            thinking={responderStreamingThinking}
                            isStreaming={responderIsThinking}
                          />
                        )}
                        <div className="text-[10px] font-light uppercase tracking-[0.08em] text-pink-600 flex items-center gap-2">
                          <span className="inline-block w-2 h-2 bg-pink-500 rounded-full animate-pulse" />
                          <span>Responding</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 py-3 md:py-4 px-4 md:px-0 md:pr-6 border-t border-border md:border-0">
        <div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <ZinsserChatInput
                onSubmit={handleSubmit}
                disabled={workflowStatus === 'submitting' || workflowStatus === 'processing' || workflowStatus === 'streaming'}
                placeholder={
                  gatekeeperTurns.length === 0
                    ? "What are you writing?"
                    : "Continue the conversation..."
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
